import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, FileText, FileDown, Trash2, Upload, Paperclip, AlertTriangle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardHeader, CardContent, CardTitle } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import {
  customerReceivedNcService, clientService, productService,
  productionLotService,
} from '../services/api';

const STATUS_COLORS = {
  'Ricevuta': 'bg-sky-500 text-white',
  'In valutazione': 'bg-amber-500 text-white',
  'In risposta': 'bg-blue-500 text-white',
  'In attesa di riscontro': 'bg-violet-500 text-white',
  'Accettata': 'bg-emerald-600 text-white',
  'Parzialmente accettata': 'bg-emerald-400 text-white',
  'Rifiutata': 'bg-red-600 text-white',
  'Chiusa': 'bg-slate-500 text-white',
};

// Blocca l'update se un campo è undefined (per non azzerare valori esistenti)
const cleanForUpdate = (obj) => {
  const out = {};
  Object.entries(obj).forEach(([k, v]) => {
    if (v !== undefined) out[k] = v === '' ? null : v;
  });
  return out;
};

export default function CustomerReceivedNcDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [nc, setNc] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [lots, setLots] = useState([]);
  const [enums, setEnums] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [upFile, setUpFile] = useState(null);
  const [upCategory, setUpCategory] = useState('Report cliente');
  const [upDescription, setUpDescription] = useState('');
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ncRes, attRes, cRes, pRes, eRes] = await Promise.all([
        customerReceivedNcService.getById(id),
        customerReceivedNcService.getAttachments(id),
        clientService.getAll(),
        productService.getAll(),
        customerReceivedNcService.enums(),
      ]);
      setNc(ncRes.data);
      setAttachments(attRes.data || []);
      setCustomers(cRes.data || []);
      setProducts(pRes.data || []);
      setEnums(eRes.data || {});
      // Load lots (best-effort)
      try {
        const lRes = await productionLotService.getAll({});
        setLots(lRes.data || []);
      } catch { /* opzionale */ }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore caricamento NC');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const patch = (updates) => setNc((prev) => ({ ...prev, ...updates }));

  const save = async () => {
    if (!nc) return;
    setSaving(true);
    try {
      const payload = cleanForUpdate({
        customer_external_nc_number: nc.customer_external_nc_number,
        received_date: nc.received_date,
        customer_id: nc.customer_id,
        product_id: nc.product_id,
        product_revision_id: nc.product_revision_id,
        production_lot_id: nc.production_lot_id,
        customer_order_reference: nc.customer_order_reference,
        delivery_reference: nc.delivery_reference,
        quantity_involved: nc.quantity_involved === '' ? null : nc.quantity_involved,
        customer_defect_description: nc.customer_defect_description,
        customer_request: nc.customer_request,
        response_deadline: nc.response_deadline,
        status: nc.status,
        internal_analysis: nc.internal_analysis,
        root_cause: nc.root_cause,
        containment_action: nc.containment_action,
        corrective_action: nc.corrective_action,
        responsible_person: nc.responsible_person,
        planned_completion_date: nc.planned_completion_date,
        completion_date: nc.completion_date,
        technical_outcome: nc.technical_outcome,
        response_text: nc.response_text,
        response_date: nc.response_date,
        response_sent_by: nc.response_sent_by,
        response_status: nc.response_status,
        customer_debit_amount_requested: nc.customer_debit_amount_requested === '' ? null : nc.customer_debit_amount_requested,
        amount_accepted_by_evotech: nc.amount_accepted_by_evotech === '' ? null : nc.amount_accepted_by_evotech,
        economic_outcome: nc.economic_outcome,
        economic_notes: nc.economic_notes,
        notes: nc.notes,
      });
      const res = await customerReceivedNcService.update(id, payload);
      setNc(res.data);
      toast.success('NC salvata');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!window.confirm(`Eliminare definitivamente ${nc.nc_code}?`)) return;
    try {
      await customerReceivedNcService.delete(id);
      toast.success('NC eliminata');
      navigate('/customer-received-ncs');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const handleUpload = async () => {
    if (!upFile) { toast.error('Seleziona un file'); return; }
    setUploading(true);
    try {
      await customerReceivedNcService.uploadAttachment(id, upFile, upCategory, upDescription);
      toast.success('Allegato caricato');
      setUploadOpen(false);
      setUpFile(null);
      setUpDescription('');
      setUpCategory('Report cliente');
      const attRes = await customerReceivedNcService.getAttachments(id);
      setAttachments(attRes.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAttachment = async (attId) => {
    if (!window.confirm('Eliminare allegato?')) return;
    try {
      await customerReceivedNcService.deleteAttachment(id, attId);
      const attRes = await customerReceivedNcService.getAttachments(id);
      setAttachments(attRes.data || []);
      toast.success('Allegato eliminato');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const openReport = () => window.open(customerReceivedNcService.reportPdfUrl(id), '_blank');
  const openDossier = () => window.open(customerReceivedNcService.dossierPdfUrl(id), '_blank');

  if (loading) return <div className="text-center py-10"><div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto" /></div>;
  if (!nc) return <div className="text-center py-10 text-slate-500">NC non trovata</div>;

  const economicActive = nc.economic_outcome && nc.economic_outcome !== 'Nessun addebito';

  return (
    <div className="space-y-6 pb-24" data-testid="crnc-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Link to="/customer-received-ncs">
            <Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-1" />Indietro</Button>
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold font-mono">{nc.nc_code}</h1>
              <Badge className={`${STATUS_COLORS[nc.status] || 'bg-slate-400 text-white'} px-3 py-1`}>{nc.status}</Badge>
            </div>
            <p className="text-slate-500 text-sm mt-1">
              {nc.customer_name || '—'} · {nc.customer_external_nc_number ? `N° cliente ${nc.customer_external_nc_number}` : 'senza N° cliente'} · ricevuta {nc.received_date}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={openReport} data-testid="print-report-btn">
            <FileText className="w-4 h-4 mr-1" /> Stampa Report NC Cliente
          </Button>
          <Button variant="outline" onClick={openDossier} data-testid="print-dossier-btn">
            <FileDown className="w-4 h-4 mr-1" /> Stampa Fascicolo Completo
          </Button>
          <Button onClick={save} disabled={saving} data-testid="save-crnc-btn">
            <Save className="w-4 h-4 mr-1" />{saving ? 'Salvo...' : 'Salva'}
          </Button>
          <Button variant="destructive" size="sm" onClick={doDelete} data-testid="delete-crnc-btn">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <Tabs defaultValue="general">
        <TabsList className="h-11 flex-wrap">
          <TabsTrigger value="general" className="text-sm px-4">Dati Generali</TabsTrigger>
          <TabsTrigger value="analysis" className="text-sm px-4">Analisi Evotech</TabsTrigger>
          <TabsTrigger value="response" className="text-sm px-4">Risposta Cliente</TabsTrigger>
          <TabsTrigger value="economic" className="text-sm px-4">Gestione Economica</TabsTrigger>
          <TabsTrigger value="attachments" className="text-sm px-4">Allegati ({attachments.length})</TabsTrigger>
        </TabsList>

        {/* GENERAL */}
        <TabsContent value="general" className="mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Dati NC Cliente</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Cliente *</Label>
                <Select value={nc.customer_id || ''} onValueChange={v => patch({ customer_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>{customers.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>N° NC Cliente (esterno)</Label>
                <Input value={nc.customer_external_nc_number || ''} onChange={e => patch({ customer_external_nc_number: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Data ricezione *</Label>
                <Input type="date" value={nc.received_date || ''} onChange={e => patch({ received_date: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Stato *</Label>
                <Select value={nc.status || ''} onValueChange={v => patch({ status: v })}>
                  <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{(enums.statuses || []).map(s => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Prodotto</Label>
                <Select value={nc.product_id || 'none'} onValueChange={v => patch({ product_id: v === 'none' ? null : v })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— Nessuno —</SelectItem>{products.map(p => (<SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Lotto / Commessa</Label>
                <Select value={nc.production_lot_id || 'none'} onValueChange={v => patch({ production_lot_id: v === 'none' ? null : v })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— Nessuno —</SelectItem>{lots.slice(0, 500).map(l => (<SelectItem key={l.id} value={l.id}>{l.lot_code} {l.product_code ? `— ${l.product_code}` : ''}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Rif. Ordine Cliente</Label>
                <Input value={nc.customer_order_reference || ''} onChange={e => patch({ customer_order_reference: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Rif. DDT / Consegna</Label>
                <Input value={nc.delivery_reference || ''} onChange={e => patch({ delivery_reference: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Quantità coinvolta</Label>
                <Input type="number" min="0" step="any" value={nc.quantity_involved ?? ''} onChange={e => patch({ quantity_involved: e.target.value === '' ? null : Number(e.target.value) })} />
              </div>
              <div className="space-y-1">
                <Label>Scadenza risposta</Label>
                <Input type="date" value={nc.response_deadline || ''} onChange={e => patch({ response_deadline: e.target.value })} />
              </div>
              <div className="col-span-2 space-y-1">
                <Label>Descrizione difetto (cliente) *</Label>
                <Textarea rows={3} value={nc.customer_defect_description || ''} onChange={e => patch({ customer_defect_description: e.target.value })} />
              </div>
              <div className="col-span-2 space-y-1">
                <Label>Richiesta del cliente</Label>
                <Textarea rows={2} value={nc.customer_request || ''} onChange={e => patch({ customer_request: e.target.value })} />
              </div>
              <div className="col-span-2 space-y-1">
                <Label>Note interne</Label>
                <Textarea rows={2} value={nc.notes || ''} onChange={e => patch({ notes: e.target.value })} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ANALISI EVOTECH */}
        <TabsContent value="analysis" className="mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Analisi Evotech</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-1">
                <Label>Analisi interna</Label>
                <Textarea rows={3} value={nc.internal_analysis || ''} onChange={e => patch({ internal_analysis: e.target.value })} />
              </div>
              <div className="col-span-2 space-y-1">
                <Label>Causa radice</Label>
                <Textarea rows={2} value={nc.root_cause || ''} onChange={e => patch({ root_cause: e.target.value })} />
              </div>
              <div className="col-span-2 space-y-1">
                <Label>Azione di contenimento</Label>
                <Textarea rows={2} value={nc.containment_action || ''} onChange={e => patch({ containment_action: e.target.value })} />
              </div>
              <div className="col-span-2 space-y-1">
                <Label>Azione correttiva</Label>
                <Textarea rows={2} value={nc.corrective_action || ''} onChange={e => patch({ corrective_action: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Responsabile</Label>
                <Input value={nc.responsible_person || ''} onChange={e => patch({ responsible_person: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Esito tecnico</Label>
                <Select value={nc.technical_outcome || 'none'} onValueChange={v => patch({ technical_outcome: v === 'none' ? null : v })}>
                  <SelectTrigger data-testid="technical-outcome-select"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— Nessuno —</SelectItem>{(enums.technical_outcomes || []).map(s => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Data prevista chiusura</Label>
                <Input type="date" value={nc.planned_completion_date || ''} onChange={e => patch({ planned_completion_date: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Data effettiva chiusura</Label>
                <Input type="date" value={nc.completion_date || ''} onChange={e => patch({ completion_date: e.target.value })} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* RISPOSTA */}
        <TabsContent value="response" className="mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Risposta al Cliente</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-1">
                <Label>Testo risposta</Label>
                <Textarea rows={6} value={nc.response_text || ''} onChange={e => patch({ response_text: e.target.value })} data-testid="response-text" />
                <p className="text-xs text-slate-500">Questo testo sarà incluso nel Report PDF inviato al cliente.</p>
              </div>
              <div className="space-y-1">
                <Label>Data risposta</Label>
                <Input type="date" value={nc.response_date || ''} onChange={e => patch({ response_date: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Inviata da</Label>
                <Input value={nc.response_sent_by || ''} onChange={e => patch({ response_sent_by: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Stato risposta</Label>
                <Select value={nc.response_status || 'Da preparare'} onValueChange={v => patch({ response_status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{(enums.response_statuses || []).map(s => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ECONOMICA */}
        <TabsContent value="economic" className="mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Gestione Economica</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Esito economico</Label>
                <Select value={nc.economic_outcome || 'Nessun addebito'} onValueChange={v => patch({ economic_outcome: v })}>
                  <SelectTrigger data-testid="economic-outcome-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{(enums.economic_outcomes || []).map(s => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div />
              <div className="space-y-1">
                <Label>Addebito richiesto dal cliente (€) {economicActive && '*'}</Label>
                <Input type="number" min="0" step="any" value={nc.customer_debit_amount_requested ?? ''} onChange={e => patch({ customer_debit_amount_requested: e.target.value === '' ? null : Number(e.target.value) })} />
              </div>
              <div className="space-y-1">
                <Label>Importo accettato da Evotech (€)</Label>
                <Input type="number" min="0" step="any" value={nc.amount_accepted_by_evotech ?? ''} onChange={e => patch({ amount_accepted_by_evotech: e.target.value === '' ? null : Number(e.target.value) })} />
              </div>
              <div className="col-span-2 space-y-1">
                <Label>Note economiche {economicActive && '*'}</Label>
                <Textarea rows={3} value={nc.economic_notes || ''} onChange={e => patch({ economic_notes: e.target.value })} />
              </div>
              {economicActive && (
                <div className="col-span-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  <span>Con esito economico diverso da &quot;Nessun addebito&quot; sono richieste anche le Note economiche.</span>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ALLEGATI */}
        <TabsContent value="attachments" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><Paperclip className="w-4 h-4" /> Allegati</CardTitle>
              <Button onClick={() => setUploadOpen(true)} data-testid="upload-attachment-btn">
                <Upload className="w-4 h-4 mr-1" /> Carica allegato
              </Button>
            </CardHeader>
            <CardContent>
              {attachments.length === 0 ? (
                <p className="text-center text-slate-500 py-8 italic">Nessun allegato caricato</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th className="px-3 py-2 text-left font-medium text-slate-600">#</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Categoria</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">File</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Descrizione</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Caricato da</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Data</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {attachments.map((a, i) => {
                      const ext = (a.file_name || '').split('.').pop().toLowerCase();
                      const printable = ['pdf', 'jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'tiff'].includes(ext);
                      return (
                        <tr key={a.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`att-row-${a.id}`}>
                          <td className="px-3 py-2 font-mono text-xs">{i + 1}</td>
                          <td className="px-3 py-2"><Badge variant="outline">{a.attachment_category}</Badge></td>
                          <td className="px-3 py-2">
                            <a href={`${process.env.REACT_APP_BACKEND_URL}${a.file_url}`} target="_blank" rel="noreferrer" className="text-sky-700 hover:underline">
                              {a.file_name}
                            </a>
                            {!printable && <Badge className="ml-2 bg-slate-100 text-slate-600 text-[10px]">Non stampabile</Badge>}
                          </td>
                          <td className="px-3 py-2 text-slate-600">{a.description || '—'}</td>
                          <td className="px-3 py-2 text-xs">{a.uploaded_by || '—'}</td>
                          <td className="px-3 py-2 text-xs font-mono">{(a.created_at || '').slice(0, 10)}</td>
                          <td className="px-3 py-2 text-right">
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteAttachment(a.id)} className="text-red-500 hover:text-red-700" data-testid={`del-att-${a.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nuovo allegato</DialogTitle><DialogDescription>Carica un file (PDF/immagine) da collegare a questa NC.</DialogDescription></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>File *</Label>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.bmp,.tiff,.doc,.docx,.xls,.xlsx,.txt,.zip"
                onChange={(e) => setUpFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-slate-700 file:mr-3 file:py-2 file:px-4 file:rounded file:border-0 file:bg-slate-900 file:text-white hover:file:bg-slate-800 file:cursor-pointer"
                data-testid="upload-file-input"
                disabled={uploading}
              />
            </div>
            <div className="space-y-1">
              <Label>Categoria *</Label>
              <Select value={upCategory} onValueChange={setUpCategory}>
                <SelectTrigger data-testid="upload-category"><SelectValue /></SelectTrigger>
                <SelectContent>{(enums.attachment_categories || []).map(c => (<SelectItem key={c} value={c}>{c}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Descrizione</Label>
              <Input value={upDescription} onChange={(e) => setUpDescription(e.target.value)} placeholder="Descrizione breve" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)} disabled={uploading}>Annulla</Button>
            <Button onClick={handleUpload} disabled={uploading || !upFile} data-testid="upload-submit">
              {uploading ? 'Upload...' : 'Carica'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
